import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SophisticatedCarousel from "@/components/SophisticatedCarousel";
import { ArrowRight, Shield, Users, Globe, TrendingUp, Building, Heart, Award, MessageCircle, CheckCircle, Star, Crown } from "lucide-react";
import { useEffect, useRef } from "react";

const Index = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Intersection Observer for sophisticated fade-in animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  // Imagens do carrossel hero (famílias nigerianas de sucesso) - ÚNICAS
  const heroCarouselImages = [
    "./images/Gemini_Generated_Image_y8l7pwy8l7pwy8l7 (1).png", // Família com chave na mão
    "./images/ChatGPT Image 4 de fev. de 2026, 22_56_44.png", // Família em casa luxo
    "./images/ChatGPT Image 4 de fev. de 2026, 19_38_54.png"  // Pais buscando filho na escola
  ];

  // Imagens do carrossel Brasil (paisagens) - ÚNICAS, DIFERENTES DO HERO
  const brazilCarouselImages = [
    "./images/CARROSSEL 01.jpg", // Brasília
    "./images/CARROSSEL 02.jpg", // São Paulo
    "./images/CARROSSEL 03.jpg", // Salvador
    "./images/CARROSSEL 04.jpeg", // Rio
    "./images/CARROSSEL 05.jpg", // Fortaleza
    "./images/CARROSSEL 06.png", // Belo Horizonte
    "./images/CARROSSEL 07.jpg", // Porto Alegre
    "./images/CARROSSEL 08.jpg", // Recife
    "./images/CARROSSEL 09.jpg"  // Curitiba
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section - Carrossel Dinâmico Cross-fade */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <SophisticatedCarousel 
          images={heroCarouselImages}
          interval={7500}
          className="absolute inset-0"
        />
        
        {/* Content Over Carousel */}
        <div className="relative z-10 text-center text-white elite-container">
          <div className="fade-in">
            <h1 className="font-luxury text-6xl lg:text-8xl font-bold mb-8 leading-tight text-shadow-strong">
              Your Legacy and Legal Residency
              <span className="block text-accent drop-shadow-lg">in Brazil Start Here</span>
            </h1>
            <p className="text-2xl lg:text-3xl mb-12 mx-auto max-w-5xl leading-relaxed text-shadow-strong">
              União Lar & Vida is a family-owned consultancy dedicated to providing secure relocation 
              through premium real estate investment in Brazil's most prestigious markets.
            </p>
            <Button asChild className="elite-button text-xl px-16 py-6 shadow-2xl">
              <Link to="#contact-consultation">
                <MessageCircle className="mr-2 h-6 w-6" />
                Apply for a Private Strategic Consultation
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Family-Owned Identity Seal */}
      <section className="py-16 bg-accent text-white text-center">
        <div className="elite-container">
          <div className="flex items-center justify-center space-x-6 fade-in">
            <Crown className="h-8 w-8" />
            <span className="font-luxury text-2xl font-semibold">
              A family-owned consultancy dedicated to excellence and integrity
            </span>
            <Crown className="h-8 w-8" />
          </div>
        </div>
      </section>

      {/* About Our Service - Expertise Multidisciplinar */}
      <section className="elite-section bg-white page-content">
        <div className="elite-container">
          <div className="text-center mb-20 fade-in">
            <h2 className="elite-heading">About Our Service</h2>
            <div className="elite-divider"></div>
            <p className="elite-subheading mx-auto">
              Founded by demanding Brazilian specialists, experts in image and sales, capable of operating 
              in 27 languages in the Brazilian market. We are not a common real estate agency. We are a 
              <strong> One-Stop-Shop</strong> that integrates Legal, Financial, Technical, and Immigration 
              expertise with Cross-Validation for your complete peace of mind.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="elite-card text-center fade-in elite-hover">
              <CardContent className="p-10">
                <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-8">
                  <Shield className="h-12 w-12 text-accent" />
                </div>
                <h3 className="font-display text-xl text-primary mb-6">Legal Matrix</h3>
                <p className="text-slate-600 leading-relaxed">
                  Complete compliance with Brazilian immigration law and international protocols for your absolute security.
                </p>
              </CardContent>
            </Card>
            
            <Card className="elite-card text-center fade-in elite-hover">
              <CardContent className="p-10">
                <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-8">
                  <TrendingUp className="h-12 w-12 text-accent" />
                </div>
                <h3 className="font-display text-xl text-primary mb-6">Financial Matrix</h3>
                <p className="text-slate-600 leading-relaxed">
                  Strategic capital allocation and investment structuring for optimal returns and comprehensive asset protection.
                </p>
              </CardContent>
            </Card>
            
            <Card className="elite-card text-center fade-in elite-hover">
              <CardContent className="p-10">
                <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-8">
                  <Building className="h-12 w-12 text-accent" />
                </div>
                <h3 className="font-display text-xl text-primary mb-6">Technical Matrix</h3>
                <p className="text-slate-600 leading-relaxed">
                  Professional property evaluation and architectural due diligence by certified experts and engineers.
                </p>
              </CardContent>
            </Card>
            
            <Card className="elite-card text-center fade-in elite-hover">
              <CardContent className="p-10">
                <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-8">
                  <Globe className="h-12 w-12 text-accent" />
                </div>
                <h3 className="font-display text-xl text-primary mb-6">Immigration Matrix</h3>
                <p className="text-slate-600 leading-relaxed">
                  End-to-end residency process management with cross-validation protocols and comprehensive support.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who We Serve - HNWIs Exclusivos */}
      <section className="elite-section bg-slate-50">
        <div className="elite-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="fade-in">
              <h2 className="elite-heading">Who We Serve</h2>
              <div className="elite-divider"></div>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                We exclusively serve <strong>High-Net-Worth Individuals (HNWIs)</strong> and distinguished families 
                seeking economic stability, comprehensive asset protection, and strategic international diversification 
                in a G20 powerhouse with unparalleled opportunities for generational wealth building.
              </p>
              <div className="space-y-8">
                <div className="flex items-start space-x-6">
                  <div className="w-6 h-6 bg-accent rounded-full mt-2 flex-shrink-0 flex items-center justify-center">
                    <Star className="h-3 w-3 text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl text-primary mb-3">Investment-Grade Clients</h3>
                    <p className="text-slate-600 leading-relaxed">Individuals with substantial liquid capital seeking strategic diversification and sophisticated wealth preservation strategies</p>
                  </div>
                </div>
                <div className="flex items-start space-x-6">
                  <div className="w-6 h-6 bg-accent rounded-full mt-2 flex-shrink-0 flex items-center justify-center">
                    <Heart className="h-3 w-3 text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl text-primary mb-3">Family Legacy Planning</h3>
                    <p className="text-slate-600 leading-relaxed">Distinguished families establishing multi-generational wealth protection and comprehensive succession planning</p>
                  </div>
                </div>
                <div className="flex items-start space-x-6">
                  <div className="w-6 h-6 bg-accent rounded-full mt-2 flex-shrink-0 flex items-center justify-center">
                    <Globe className="h-3 w-3 text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl text-primary mb-3">Global Mobility Seekers</h3>
                    <p className="text-slate-600 leading-relaxed">Sophisticated professionals requiring international residence flexibility and premium lifestyle enhancement</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative fade-in">
              <img 
                src="./images/ChatGPT Image 4 de fev. de 2026, 22_34_00.png" 
                alt="Distinguished Nigerian family planning their Brazilian investment" 
                className="rounded-lg shadow-2xl w-full elite-hover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Brazil: A Global Safe Harbor - Módulo Imersivo com Parallax */}
      <section className="relative elite-section">
        <SophisticatedCarousel 
          images={brazilCarouselImages}
          interval={8000}
          className="absolute inset-0"
        />
        
        {/* Conteúdo Flutuante sobre Parallax */}
        <div className="relative z-10 elite-container text-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center min-h-screen">
            <div className="fade-in">
              <h2 className="font-luxury text-5xl lg:text-7xl font-bold mb-8 leading-tight text-shadow-strong">
                Brazil: A Global
                <span className="block text-accent">Safe Harbor</span>
              </h2>
              <div className="space-y-8">
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20">
                  <h3 className="font-display text-2xl text-accent mb-4">G20 Economic Powerhouse</h3>
                  <p className="text-xl leading-relaxed text-shadow-strong">
                    Brazil stands as the world's 9th largest economy, the undisputed leader of Latin America, 
                    and a global pioneer in agribusiness and digital innovation with PIX and Open Banking systems.
                  </p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20">
                  <h3 className="font-display text-2xl text-accent mb-4">The Largest African Nation Outside Africa</h3>
                  <p className="text-xl leading-relaxed text-shadow-strong">
                    Brazil offers an unparalleled environment of cultural connection, ancestral familiarity, 
                    and social integration that creates a natural bridge for Nigerian investors seeking belonging and prosperity.
                  </p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20">
                  <h3 className="font-display text-2xl text-accent mb-4">Family Reunification Guarantee</h3>
                  <p className="text-xl leading-relaxed text-shadow-strong">
                    Brazilian law constitutionally guarantees Family Reunification rights, ensuring your investment 
                    protects your entire lineage with full access to education, healthcare, and business opportunities.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="fade-in text-center">
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-12 border border-white/20">
                <Award className="h-20 w-20 text-accent mx-auto mb-8" />
                <h3 className="font-luxury text-4xl text-accent mb-6">Strategic Markets</h3>
                <div className="space-y-6 text-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">Brasília</span>
                    <span className="text-accent">Institutional Stability</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">São Paulo</span>
                    <span className="text-accent">Financial Capital</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">Salvador</span>
                    <span className="text-accent">Cultural Heritage</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment to Excellence */}
      <section className="elite-section bg-white">
        <div className="elite-container">
          <div className="text-center mb-20 fade-in">
            <h2 className="elite-heading">Our Commitment to Excellence</h2>
            <div className="elite-divider"></div>
            <p className="elite-subheading mx-auto">
              Founded by demanding Brazilian specialists with uncompromising standards, we deliver 
              sophisticated solutions that exceed the expectations of the world's most discerning investors.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center fade-in">
              <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <Globe className="h-12 w-12 text-accent" />
              </div>
              <h3 className="font-display text-2xl text-primary mb-6">27 Languages</h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                Multilingual expertise ensuring seamless communication and cultural understanding 
                throughout your investment journey in the Brazilian market.
              </p>
            </div>
            
            <div className="text-center fade-in">
              <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <Shield className="h-12 w-12 text-accent" />
              </div>
              <h3 className="font-display text-2xl text-primary mb-6">Cross-Validation</h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                Our proprietary Cross-Validation system ensures every aspect of your investment 
                is verified across multiple expert matrices for absolute security.
              </p>
            </div>
            
            <div className="text-center fade-in">
              <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <Award className="h-12 w-12 text-accent" />
              </div>
              <h3 className="font-display text-2xl text-primary mb-6">Elite Standards</h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                Uncompromising commitment to excellence in image, sales, and service delivery 
                that matches the sophistication of our distinguished clientele.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Checkpoint Zero */}
      <section id="contact-consultation" className="relative elite-section">
        <div className="absolute inset-0 z-0">
          <img 
            src="./images/ChatGPT Image 4 de fev. de 2026, 23_12_24.png"
            alt="Your Brazilian legacy awaits - Private strategic consultation" 
            className="w-full h-full object-cover"
          />
          <div className="sophisticated-overlay"></div>
        </div>
        <div className="relative z-10 elite-container text-center text-white">
          <div className="fade-in">
            <h2 className="font-luxury text-6xl lg:text-7xl font-bold mb-8 text-shadow-strong">
              Ready to Secure Your
              <span className="block text-accent">Family's Legacy?</span>
            </h2>
            <div className="elite-divider bg-accent"></div>
            <p className="text-2xl lg:text-3xl mb-16 max-w-5xl mx-auto leading-relaxed text-shadow-strong">
              Schedule a confidential consultation with our investment advisors to discuss 
              your Brazilian residency strategy and comprehensive wealth protection plan.
            </p>
            <Button asChild className="elite-button text-2xl px-20 py-8 shadow-2xl">
              <Link to="/contact">
                <MessageCircle className="mr-3 h-8 w-8" />
                Apply for a Private Strategic Consultation
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* WhatsApp Flutuante Premium */}
      <div className="fixed bottom-8 right-8 z-50">
        <Button asChild className="bg-green-500 hover:bg-green-600 text-white rounded-full w-20 h-20 shadow-2xl elite-hover">
          <Link to="/contact" aria-label="Private Investment Consultation via WhatsApp">
            <MessageCircle className="h-10 w-10" />
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default Index;